import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  ChevronRightIcon,
  PhoneIcon,
  ClockIcon,
  CheckIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import NavBar from '../features/navbar/Navbar';
import Footer from '../features/common/Footer';
import { useLanguage } from '../context/LanguageContext';
import { useContactTranslations } from '../context/contactTranslations';
import MainLayout from '../components/MainLayout';

function Contact() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useContactTranslations(language);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      setTimeout(() => {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: 'general',
          message: '',
        });
        setLoading(false);
        setTimeout(() => setSubmitted(false), 5000);
      }, 1500);
    } catch (error) {
      console.error('Error submitting form:', error);
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: EnvelopeIcon,
      title: t.emailSupport,
      info: t.emailAddress,
      description: t.emailDescription,
      color: 'from-blue-500 to-sky-500',
      link: 'mailto:contact@subifyhub.com',
      isExternal: true,
    },
    {
      icon: ChatBubbleLeftIcon,
      title: t.whatsappTitle,
      info: t.whatsappNumber,
      description: t.whatsappDescription,
      color: 'from-green-500 to-emerald-500',
      link: 'https://wa.me/923272341839',
      appLink: 'whatsapp://send?phone=923272341839',
      isExternal: true,
    },
    {
      icon: PhoneIcon,
      title: t.telegramTitle,
      info: t.telegramNumber,
      description: t.telegramDescription,
      color: 'from-sky-400 to-blue-500',
      link: 'https://t.me/subifyhub_support',
      appLink: 'tg://resolve?domain=subifyhub_support',
      isExternal: true,
    },
  ];

  const faqItems = [
    {
      question: t.q1,
      answer: t.a1,
    },
    {
      question: t.q2,
      answer: t.a2,
    },
    {
      question: t.q3,
      answer: t.a3,
    },
    {
      question: t.q4,
      answer: t.a4,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <MainLayout>
      <NavBar>
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
        {/* Hero Section */}
        <motion.div
          className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 py-16 sm:py-20 lg:py-28"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-400/30 text-blue-100 mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <SparklesIcon className="w-8 h-8" />
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {t.pageTitle}
            </motion.h1>

            <motion.p
              className="text-lg sm:text-xl text-blue-50 max-w-3xl mx-auto font-light leading-relaxed"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {t.pageSubtitle}
            </motion.p>
          </div>
        </motion.div>

        {/* Introduction Section */}
        <motion.div
          className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 sm:p-10 border border-slate-200"
            variants={itemVariants}
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              {t.introTitle}
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              {language === 'en' ? (
                <>
                  At <span className="font-semibold text-blue-600">SubifyHub</span>, we pride ourselves on being accessible and responsive. Whether you need assistance with your subscription, want to learn more about our services, or have valuable feedback to share, our customer support team is here for you 24/7. We're committed to providing you with the best experience possible.
                </>
              ) : (
                <>
                  Chez <span className="font-semibold text-blue-600">SubifyHub</span>, nous sommes fiers d'être accessibles et réactifs. Que vous ayez besoin d'assistance avec votre abonnement, que vous souhaitiez en savoir plus sur nos services, ou que vous ayez des commentaires précieux à partager, notre équipe d'assistance clientèle est là pour vous 24/7. Nous nous engageons à vous offrir la meilleure expérience possible.
                </>
              )}
            </p>
          </motion.div>
        </motion.div>

        {/* Contact Methods Section */}
        <motion.div
          className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-12 text-center"
            variants={itemVariants}
          >
            {t.contactMethodsTitle}
          </motion.h2>

          <motion.div
            className="grid md:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {contactMethods.map((method, idx) => (
              <motion.div
                key={idx}
                onClick={() => {
                  // Try to open app with deep link first (for WhatsApp/Telegram)
                  if (method.appLink && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                    window.location.href = method.appLink;
                  } else if (method.appLink && !method.appLink.startsWith('mailto')) {
                    // Desktop - try app link first
                    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                    if (isSafari && method.appLink.startsWith('whatsapp')) {
                      window.location.href = method.link;
                    } else {
                      window.location.href = method.appLink;
                      // Fallback to web version after a delay if app doesn't open
                      setTimeout(() => {
                        window.location.href = method.link;
                      }, 1000);
                    }
                  } else {
                    // Email - always use mailto
                    window.location.href = method.link;
                  }
                }}
                className="group cursor-pointer"
                variants={itemVariants}
              >
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-slate-200 hover:border-blue-300 h-full cursor-pointer transform hover:scale-105">
                  <div className="flex justify-center mb-6">
                    <div
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${method.color} text-white shadow-lg transform group-hover:scale-110 transition-transform`}
                    >
                      <method.icon className="h-8 w-8" />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-3 text-center">
                    {method.title}
                  </h3>

                  <p className="text-lg font-bold text-blue-600 text-center mb-4 group-hover:text-blue-700 transition-colors">
                    {method.info}
                  </p>

                  <p className="text-slate-600 text-center leading-relaxed mb-6">
                    {method.description}
                  </p>

                  <div className="flex items-center justify-center gap-2 text-blue-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>{t.getInTouch}</span>
                    <ChevronRightIcon className="h-4 w-4" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Availability Info */}
          <motion.div
            className="mt-12 bg-gradient-to-r from-blue-50 to-blue-50 rounded-2xl border-2 border-blue-200 p-8 text-center"
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="flex justify-center mb-4">
              <ClockIcon className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">
              {t.availabilityTitle}
            </h3>
            <p className="text-slate-600 max-w-2xl mx-auto">
              <strong>{t.emailResponseTime}</strong><br />
              <strong>{t.chatResponseTime}</strong><br />
              {t.emergencySupport}
            </p>
          </motion.div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-12 text-center"
            variants={itemVariants}
          >
            {t.lookingForAnswers}
          </motion.h2>

          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {faqItems.map((item, idx) => (
              <motion.div
                key={idx}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 border border-slate-200"
                variants={itemVariants}
              >
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-blue-100 text-blue-600">
                      <QuestionMarkCircleIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      {item.question}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="mt-10 text-center"
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <button
              onClick={() => navigate('/faq')}
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-8 rounded-xl transition-all duration-200 transform hover:scale-105"
            >
              {t.viewAllFaqs}
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </motion.div>
        </motion.div>

        {/* Trust Section */}
        <motion.div
          className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 sm:p-10 relative overflow-hidden border border-blue-200"
            variants={itemVariants}
          >
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-1/4 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 flex items-start gap-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-blue-600 text-white flex-shrink-0">
                <ShieldCheckIcon className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                  {t.committedTitle}
                </h2>
                <p className="text-slate-700 text-base leading-relaxed">
                  {t.committedText}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Final CTA Section */}
        <motion.div
          className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6"
            variants={itemVariants}
          >
            {t.finalCtaTitle}
          </motion.h2>

          <motion.p
            className="text-slate-600 text-lg mb-10 max-w-2xl mx-auto leading-relaxed"
            variants={itemVariants}
          >
            {t.finalCtaText}
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.a
              href="mailto:contact@subifyhub.com"
              className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-8 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              variants={itemVariants}
            >
              <EnvelopeIcon className="h-5 w-5" />
              {t.emailUsNow}
            </motion.a>

            <motion.a
              href="https://wa.me/923272341839"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-8 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              variants={itemVariants}
            >
              <ChatBubbleLeftIcon className="h-5 w-5" />
              {t.whatsappUs}
            </motion.a>

            <motion.a
              href="https://t.me/923272341839"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-8 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              variants={itemVariants}
            >
              <PhoneIcon className="h-5 w-5" />
              {t.telegramUs}
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
      <Footer />
    </NavBar>
    </MainLayout>
  );
}

export default Contact;
